import Tag, { type TagType } from "@/components/Tag/Tag";
import type { TagExtras } from "@/components/Table/buildTagColumn";
import type { Action } from "@/components/MenuButton";
import { getOverdueDays } from "@/lib/dateHelpers";
import { TaskStageValues } from "@/types";
import type { CRMTask, TaskStage } from "@/types";

export function getOpenTaskOverdueTagExtras(task: Pick<CRMTask, "stage" | "dueDate">): TagExtras {
  const overdueDays = task.stage === TaskStageValues.OPEN ? getOverdueDays(task.dueDate) : 0;
  if (overdueDays <= 0) return {};
  return {
    iconName: "schedule",
    tooltipText: `Overdue by ${overdueDays} ${overdueDays === 1 ? "day" : "days"}`
  };
}

export const STAGE_DISPLAY: Record<string, string> = {
  [TaskStageValues.OPEN]: "Open",
  [TaskStageValues.COMPLETE]: "Complete",
  [TaskStageValues.NOT_DOING]: "Not Doing"
};

export const STAGE_TAG_TYPE: Record<string, TagType> = {
  [TaskStageValues.OPEN]: "information",
  [TaskStageValues.COMPLETE]: "success",
  [TaskStageValues.NOT_DOING]: "surface"
};

export function getStageTag(task: CRMTask) {
  const label = STAGE_DISPLAY[task.stage] || task.stage;
  const { iconName, tooltipText } = getOpenTaskOverdueTagExtras(task);
  const tagType = STAGE_TAG_TYPE[task.stage] || "surface";
  return (
    <Tag tagType={tagType} tagStyle="strong" size="small" iconName={iconName} tooltipText={tooltipText}>
      {label}
    </Tag>
  );
}

interface StageActions {
  completeTask: () => void;
  markIncomplete: () => void;
  markNotDoing: () => void;
  reopenTask: () => void;
}

export function getStageActions(stage: TaskStage, actions: StageActions): Action[] {
  switch (stage) {
    case TaskStageValues.OPEN:
      return [
        {
          text: "Mark Task Complete",
          variant: "filled",
          onClick: actions.completeTask,
          testId: "complete-task-button"
        },
        {
          text: "Mark as Not Doing",
          menuLabel: (
            <>
              Mark as <strong>Not Doing</strong>
            </>
          ),
          variant: "outlined",
          onClick: actions.markNotDoing,
          testId: "mark-not-doing-button",
          section: "Manage"
        }
      ];
    case TaskStageValues.COMPLETE:
      return [
        {
          text: "Mark Task Incomplete",
          variant: "outlined",
          onClick: actions.markIncomplete,
          testId: "mark-incomplete-button",
          section: "Manage"
        }
      ];
    case TaskStageValues.NOT_DOING:
      return [
        {
          text: "Reopen Task",
          variant: "filled",
          onClick: actions.reopenTask,
          testId: "reopen-task-button"
        }
      ];
    default:
      return [];
  }
}

export interface StageSplitAction {
  primary: Action | null;
  alternatives: Action[];
}

/** Header view of stage actions: first stage action as primary, the rest as alternatives. */
export function getStageSplitAction(stage: TaskStage, actions: StageActions): StageSplitAction {
  const [primary = null, ...alternatives] = getStageActions(stage, actions);
  return { primary, alternatives };
}
